/**
 * Statusleiste (PV-B28, PV-B29, PV-B15, PV-B20).
 *
 * Kein stilles Umschalten: Ob mit lokalem oder aktuellem Stand gearbeitet wird,
 * wie viele Aenderungen offen sind und wie es um den Speicher steht, ist immer
 * sichtbar. Wer das versteckt, laesst den Vertrieb im Glauben, alles sei
 * uebertragen.
 */

import type { Abgleichzustand } from '../daten/abgleich.ts';
import { alsMb, speicherWirdKnapp, type Speicherlage } from '../daten/speicher.ts';

interface Eigenschaften {
  online: boolean;
  zustand: Abgleichzustand;
  offen: number;
  konflikte: number;
  speicher: Speicherlage | null;
  version: string;
  neueVersionDa: boolean;
  darfAktualisieren: boolean;
  onAbgleich: () => void;
  onAktualisieren: () => void;
  onChangelog: () => void;
  onKonflikte: () => void;
  /** Offene Rueckmeldungen — die Zahl steht am Knopf. */
  offeneTickets: number;
  onTickets: () => void;
  onEinstellungen: () => void;
  onAbmelden: () => void;
}

const ZUSTANDSTEXT: Record<Abgleichzustand, string> = {
  offen: 'noch nicht abgeglichen',
  uebertraegt: 'überträgt …',
  abgeglichen: 'abgeglichen',
  fehlgeschlagen: 'Abgleich fehlgeschlagen',
};

export function Statusleiste(e: Eigenschaften): React.JSX.Element {
  const knapp = e.speicher ? speicherWirdKnapp(e.speicher) : false;

  return (
    <div className="statusleiste">
      <span className="wortmarke">
        WANIA <b>PV</b>
      </span>

      <span className={`pille ${e.online ? '' : 'pille--warnung'}`}>
        <span className={`punkt ${e.online ? 'punkt--online' : 'punkt--offline'}`} />
        {e.online ? 'Verbunden' : 'Ohne Verbindung'}
      </span>

      <span
        className={`pille ${e.zustand === 'fehlgeschlagen' || e.konflikte > 0 ? 'pille--warnung' : ''}`}
      >
        {e.offen > 0 && <span className="punkt punkt--offen" />}
        {ZUSTANDSTEXT[e.zustand]}
        {e.offen > 0 ? ` · ${e.offen} offen` : ''}
      </span>

      {/* Die Zahl allein half niemandem — sie führt jetzt dorthin, wo sich der
          Konflikt erledigen lässt. */}
      {e.konflikte > 0 && (
        <button
          className="pille pille--warnung"
          onClick={e.onKonflikte}
          style={{ minHeight: 0, cursor: 'pointer' }}
          title="Ansehen und entscheiden"
        >
          {e.konflikte} Konflikt(e) — erledigen
        </button>
      )}

      {e.speicher && (
        <span className={`pille ${knapp ? 'pille--warnung' : ''}`}>
          Speicher {alsMb(e.speicher.belegtBytes)} von {alsMb(e.speicher.verfuegbarBytes)}
          {knapp ? ' — wird knapp' : ''}
        </span>
      )}

      {e.speicher && e.speicher.beantwortbar && !e.speicher.dauerhaft && (
        <span
          className="pille pille--warnung"
          title="Der Browser hat dauerhaften Speicher abgelehnt. Für den Außendienst ist das Gerät damit nicht verlässlich."
        >
          Speicher nicht dauerhaft
        </span>
      )}

      <span className="rechts">
        {e.neueVersionDa && (
          <button
            className="wichtig"
            onClick={e.onAktualisieren}
            disabled={!e.darfAktualisieren}
            title={
              e.darfAktualisieren
                ? 'Neue Version übernehmen'
                : 'Erst abgleichen — es sind noch Änderungen offen'
            }
          >
            Neue Version
          </button>
        )}
        <button onClick={e.onAbgleich} disabled={!e.online || e.zustand === 'uebertraegt'}>
          Abgleichen
        </button>
        <button
          className="schlicht marke"
          onClick={e.onChangelog}
          style={{ margin: 0 }}
          title="Was sich geändert hat"
        >
          v{e.version}
        </button>
        <button onClick={e.onTickets} title="Fehler, Wunsch oder Frage melden">
          Rückmeldung{e.offeneTickets > 0 ? ` (${e.offeneTickets})` : ''}
        </button>
        <button onClick={e.onEinstellungen}>Einstellungen</button>
        <button className="schlicht" onClick={e.onAbmelden}>
          Abmelden
        </button>
      </span>
    </div>
  );
}
